import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Ticket, Users, Send, Clock, Plus,
  ArrowRight, CheckCircle2, AlertCircle, Zap,
  Trophy, Crown, Medal, TrendingUp,
  ExternalLink, Building2, Activity,
  Check, X as XIcon, Minus, CircleDot
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentCoupons, setRecentCoupons] = useState([]);
  const [botInfo, setBotInfo] = useState(null);
  const [activeUsers, setActiveUsers] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [resultStats, setResultStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, couponsRes, botRes, usersRes, leaderRes, sponsorsRes, resultRes] = await Promise.all([
        axios.get('/api/stats'),
        axios.get('/api/coupons'),
        axios.get('/api/telegram/bot-info'),
        axios.get('/api/settings/active-users'),
        axios.get('/api/leaderboard'),
        axios.get('/api/sponsors'),
        axios.get('/api/coupons/stats/results'),
      ]);
      setStats(statsRes.data);
      setRecentCoupons(couponsRes.data.slice(0, 8));
      setBotInfo(botRes.data);
      setActiveUsers(usersRes.data.count);
      setLeaderboard(leaderRes.data);
      setSponsors(sponsorsRes.data);
      setResultStats(resultRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-300" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 flex items-center justify-center text-xs font-bold text-muted-foreground">#{rank}</span>;
  };

  const getBadgeStyle = (badge) => {
    switch (badge) {
      case 'legend': return 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/25';
      case 'expert': return 'bg-purple-500/15 text-purple-400 border border-purple-500/25';
      case 'pro': return 'bg-blue-500/15 text-blue-400 border border-blue-500/25';
      case 'rising': return 'bg-primary/15 text-primary border border-primary/25';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getResultIcon = (result) => {
    switch (result) {
      case 'won': return <Check className="w-4 h-4 text-green-400" />;
      case 'lost': return <XIcon className="w-4 h-4 text-red-400" />;
      case 'void': return <Minus className="w-4 h-4 text-yellow-400" />;
      default: return <CircleDot className="w-4 h-4 text-blue-400" />;
    }
  };

  const getResultBadge = (result) => {
    switch (result) {
      case 'won': return 'bg-green-500/15 text-green-400 border border-green-500/25';
      case 'lost': return 'bg-red-500/15 text-red-400 border border-red-500/25';
      case 'void': return 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/25';
      default: return 'bg-blue-500/15 text-blue-400 border border-blue-500/25';
    }
  };

  const getResultLabel = (result) => {
    switch (result) {
      case 'won': return 'Won';
      case 'lost': return 'Lost';
      case 'void': return 'Void';
      default: return 'Pending';
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24 w-full" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 animate-fadeInUp">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
            Dashboard
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Welcome to your coupon management panel</p>
        </div>
        <Button
          data-testid="dashboard-create-coupon-btn"
          onClick={() => navigate('/create')}
          className="hidden md:flex gap-2"
        >
          <Plus className="w-4 h-4" />
          Create Coupon
        </Button>
      </div>

      {/* Active Users + Win Rate Banner */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="bg-gradient-to-r from-primary/10 via-card/80 to-[hsl(198,85%,55%)]/10 border-primary/20 overflow-hidden">
          <CardContent className="p-5 md:p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center">
                  <Activity className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Active Users</p>
                  <div className="flex items-baseline gap-2">
                    <p data-testid="active-users-count" className="text-4xl md:text-5xl font-bold text-foreground tabular-nums" style={{ fontFamily: 'Space Grotesk' }}>
                      {activeUsers.toLocaleString()}
                    </p>
                    <span className="text-sm text-muted-foreground">Total</span>
                  </div>
                </div>
              </div>
              <div className="hidden md:flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                <span className="text-sm text-primary font-medium">Live</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Win Rate Card */}
        {resultStats && (
          <Card className="bg-card/80 border-border/60 backdrop-blur overflow-hidden">
            <CardContent className="p-5 md:p-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-muted-foreground font-medium">Coupon Performance</p>
                <span className="text-xs text-muted-foreground">{resultStats.total} total</span>
              </div>
              <div className="flex items-center gap-6">
                <div>
                  <p className="text-3xl md:text-4xl font-bold text-primary tabular-nums" style={{ fontFamily: 'Space Grotesk' }}>
                    {resultStats.win_rate}%
                  </p>
                  <p className="text-xs text-muted-foreground">Win Rate</p>
                </div>
                <div className="flex-1 grid grid-cols-3 gap-2">
                  <div className="text-center p-2 rounded-lg bg-green-500/10">
                    <p className="text-lg font-bold text-green-400 tabular-nums">{resultStats.won}</p>
                    <p className="text-[10px] text-green-400/70">Won</p>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-red-500/10">
                    <p className="text-lg font-bold text-red-400 tabular-nums">{resultStats.lost}</p>
                    <p className="text-[10px] text-red-400/70">Lost</p>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-blue-500/10">
                    <p className="text-lg font-bold text-blue-400 tabular-nums">{resultStats.pending}</p>
                    <p className="text-[10px] text-blue-400/70">Pending</p>
                  </div>
                </div>
              </div>
              {/* Win rate bar */}
              <div className="mt-3 h-2 rounded-full bg-muted/30 overflow-hidden flex">
                <div className="h-full bg-green-500 rounded-l-full" style={{ width: `${resultStats.won / resultStats.total * 100}%` }} />
                <div className="h-full bg-red-500" style={{ width: `${resultStats.lost / resultStats.total * 100}%` }} />
                <div className="h-full bg-blue-500 rounded-r-full" style={{ width: `${resultStats.pending / resultStats.total * 100}%` }} />
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats && [
          { label: 'Total Coupons', value: stats.total_coupons, icon: Ticket, color: 'text-primary', testId: 'dashboard-total-coupons' },
          { label: 'Subscribers', value: stats.active_subscribers, icon: Users, color: 'text-[hsl(198,85%,55%)]', testId: 'dashboard-total-subscribers' },
          { label: 'Total Sent', value: stats.total_sent, icon: Send, color: 'text-[hsl(38,92%,50%)]', testId: 'dashboard-sent-messages' },
          { label: 'Sent Today', value: stats.today_sent, icon: Clock, color: 'text-primary', testId: 'dashboard-today-sent' },
        ].map((stat) => (
          <Card key={stat.label} className="stat-card bg-card/80 border-border/60 backdrop-blur">
            <CardContent className="p-4 md:p-5">
              <div className="flex items-center justify-between mb-3">
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
                <span className="text-xs text-muted-foreground">{stat.label}</span>
              </div>
              <p data-testid={stat.testId} className="text-2xl md:text-3xl font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk' }}>
                {stat.value}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Coupons with Results + Leaderboard */}
      <div className="grid lg:grid-cols-3 gap-4 md:gap-5">
        {/* Recent Coupons */}
        <Card className="lg:col-span-1 bg-card/80 border-border/60 backdrop-blur">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk' }}>Recent Coupons</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/coupons')} className="gap-1 text-xs">
                View All <ArrowRight className="w-3 h-3" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div data-testid="dashboard-recent-coupons-table" className="space-y-2">
              {recentCoupons.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Ticket className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No coupons yet</p>
                </div>
              ) : (
                recentCoupons.map((coupon) => (
                  <div
                    key={coupon.id}
                    className="flex items-center justify-between p-2.5 rounded-lg bg-muted/20 hover:bg-muted/40 transition-colors duration-150"
                  >
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      {getResultIcon(coupon.result)}
                      <div className="min-w-0">
                        <p className="text-xs font-medium truncate">
                          {coupon.home_team} vs {coupon.away_team}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {coupon.league}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                      {coupon.score && (
                        <span className="text-xs font-mono font-bold tabular-nums">{coupon.score}</span>
                      )}
                      <span className="text-xs font-mono tabular-nums text-primary">{coupon.odds}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${getResultBadge(coupon.result)}`}>
                        {getResultLabel(coupon.result)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Leaderboard */}
        <Card className="lg:col-span-2 bg-card/80 border-border/60 backdrop-blur">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-400" />
                <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk' }}>Top 10 Tipsters</CardTitle>
              </div>
              <span className="text-xs text-muted-foreground">Leaderboard</span>
            </div>
          </CardHeader>
          <CardContent>
            <div data-testid="leaderboard-table" className="space-y-2">
              {leaderboard.map((tipster, index) => (
                <div
                  key={tipster.id || index}
                  className={`flex items-center justify-between p-3 rounded-lg transition-colors duration-150 ${
                    tipster.rank <= 3
                      ? 'bg-yellow-500/5 hover:bg-yellow-500/10 border border-yellow-500/10'
                      : 'bg-muted/20 hover:bg-muted/40'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {getRankIcon(tipster.rank)}
                    <div>
                      <p className="text-sm font-semibold">{tipster.name}</p>
                      <div className="flex items-center gap-2">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${getBadgeStyle(tipster.badge)}`}>
                          {tipster.badge}
                        </span>
                        <span className="text-[10px] text-muted-foreground">{tipster.total_tips} tips</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right hidden sm:block">
                      <p className="text-xs text-muted-foreground">Win Rate</p>
                      <p className="text-sm font-bold text-primary tabular-nums">{tipster.win_rate}%</p>
                    </div>
                    <div className="text-right hidden sm:block">
                      <p className="text-xs text-muted-foreground">Profit</p>
                      <p className="text-sm font-bold text-[hsl(151,55%,44%)] tabular-nums">{tipster.profit}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Streak</p>
                      <div className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3 text-primary" />
                        <p className="text-sm font-bold tabular-nums">{tipster.streak}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sponsors */}
      <Card className="bg-card/80 border-border/60 backdrop-blur">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[hsl(38,92%,50%)]" />
              <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk' }}>Recommended Sponsors</CardTitle>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/sponsors')} className="gap-1 text-xs">
              Manage <ArrowRight className="w-3 h-3" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {sponsors.map((sponsor) => (
              <a
                key={sponsor.id}
                href={sponsor.website}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col items-center gap-2 p-4 rounded-xl bg-muted/20 hover:bg-muted/40 border border-border/40 hover:border-primary/30 transition-all duration-200"
              >
                {sponsor.logo_url ? (
                  <img src={`${BACKEND_URL}${sponsor.logo_url}`} alt={sponsor.name} className="w-12 h-12 rounded-lg object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <span className="text-lg font-bold text-primary" style={{ fontFamily: 'Space Grotesk' }}>{sponsor.name.charAt(0)}</span>
                  </div>
                )}
                <div className="text-center">
                  <p className="text-sm font-semibold group-hover:text-primary transition-colors">{sponsor.name}</p>
                  <p className="text-[10px] text-muted-foreground">{sponsor.description}</p>
                </div>
                <ExternalLink className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Bottom: Bot + Quick Actions */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="bg-card/80 border-border/60 backdrop-blur">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Telegram Bot</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {botInfo?.is_connected ? (
                <><CheckCircle2 className="w-4 h-4 text-primary" /><span className="text-sm text-primary">Connected</span></>
              ) : (
                <><AlertCircle className="w-4 h-4 text-destructive" /><span className="text-sm text-destructive">Disconnected</span></>
              )}
            </div>
            {botInfo?.username && <p className="text-xs text-muted-foreground mt-1">@{botInfo.username}</p>}
          </CardContent>
        </Card>
        <Card className="bg-card/80 border-border/60 backdrop-blur">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Button data-testid="quick-action-create" variant="outline" size="sm" className="gap-2 text-sm" onClick={() => navigate('/create')}>
              <Plus className="w-4 h-4 text-primary" /> New Coupon
            </Button>
            <Button data-testid="quick-action-coupons" variant="outline" size="sm" className="gap-2 text-sm" onClick={() => navigate('/coupons')}>
              <Send className="w-4 h-4 text-[hsl(198,85%,55%)]" /> Manage Coupons
            </Button>
            <Button data-testid="quick-action-subscribers" variant="outline" size="sm" className="gap-2 text-sm" onClick={() => navigate('/subscribers')}>
              <Users className="w-4 h-4 text-[hsl(38,92%,50%)]" /> Subscribers
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Mobile FAB */}
      <div className="md:hidden fixed bottom-6 right-6 z-50">
        <Button size="lg" className="rounded-full w-14 h-14 shadow-lg shadow-primary/25" onClick={() => navigate('/create')}>
          <Plus className="w-6 h-6" />
        </Button>
      </div>
    </div>
  );
}
